import React, { useState, useEffect } from 'react';
import { FaBars, FaTimes, FaDoorOpen } from 'react-icons/fa';
import StagePlayer from '../UnityGame/StagePlayer';
import IVSRealtimeChat from '../UnityGame/IVSRealtimeChat';
import API from '../../API';

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
  stage_playback_url?: string;
  spawn_position?: string;
  spawn_rotation?: string;
  podium_position?: string;
}

interface AgendaItem {
  id: string;
  event_id: string;
  title: string;
  subtitle: string;
  time_utc: string;
  status: string;
  room_id?: string;
  duration_minutes?: number; // Optional duration to determine when item ends
}

interface IntersectEventMobileProps {
  accessToken: string;
}

// Call room links - these open Google Meet in new tab
const callRoomLinks: { [key: string]: string } = {
  'call-room-enter-1': 'https://meet.google.com/wdg-nbqh-vxu',
  'call-room-enter-2': 'https://meet.google.com/kgt-pgui-ide',
  'call-room-enter-3': 'https://meet.google.com/ygj-pdpz-tan',
};

// Create virtual call room objects to append to rooms list
const callRooms: Room[] = [
  {
    room_id: 'call-room-enter-1',
    room_name: 'Call Room 1',
    event_id: '22222',
    chat_room_arn: '',
    stage_arn: '',
  },
  {
    room_id: 'call-room-enter-2',
    room_name: 'Call Room 2',
    event_id: '22222',
    chat_room_arn: '',
    stage_arn: '',
  },
  {
    room_id: 'call-room-enter-3',
    room_name: 'Call Room 3',
    event_id: '22222',
    chat_room_arn: '',
    stage_arn: '',
  },
];

const IntersectEventMobile: React.FC<IntersectEventMobileProps> = ({ accessToken }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  // const [chatRoomArn, setChatRoomArn] = useState<string>('');
  // const [userId, setUserId] = useState<string>('');
  const [isStageMuted, setIsStageMuted] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(false);

  const eventId = '22222';

  // Fetch rooms on mount
  useEffect(() => {
    let isMounted = true;

    const fetchRooms = async () => {
      try {
        const response = await API.getRooms(accessToken, eventId);
        const roomsData = await response.json();
        
        if (isMounted) {
          // Append call rooms to the fetched rooms
          const allRooms = [...(roomsData || []), ...callRooms];
          setRooms(allRooms);
          
          // Set default room to intersect-main
          const mainRoom = roomsData.find((r: Room) => r.room_id === 'intersect-main');
          if (mainRoom) {
            setCurrentRoom(mainRoom);
            // setChatRoomArn(mainRoom.chat_room_arn);
          }
        }
      } catch (error) {
        console.error('[Mobile] Error fetching rooms:', error);
      }
    };

    fetchRooms();

    return () => {
      isMounted = false;
    };
  }, [accessToken, eventId]);

  // Fetch agenda items
  // useEffect(() => {
  //   let isMounted = true;

  //   const fetchAgenda = async () => {
  //     try {
  //       const response = await API.getAgenda(accessToken, eventId);
  //       const agendaData = await response.json();
        
  //       if (isMounted) {
  //         setAgendaItems(agendaData || []);
  //       }
  //     } catch (error) {
  //       console.error('[Mobile] Error fetching agenda:', error);
  //     }
  //   };

  //   fetchAgenda();

  //   return () => {
  //     isMounted = false;
  //   };
  // }, [accessToken, eventId]);

  // Fetch chat token when room changes
  useEffect(() => {
    if (!currentRoom) return;

    let isMounted = true;

    // const fetchChatToken = async () => {
    //   try {
    //     if (isMounted) {
    //       setLoading(true);
    //     }
        
    //     const response = await API.getIVSChatTokenForRoom(accessToken, {
    //       eventId: eventId,
    //       roomId: currentRoom.room_id
    //     });
    //     const data = await response.json();
        
    //     if (isMounted) {
    //       setUserId(data.userId || data.username || 'mobile-user');
          
    //       console.log('[Mobile] Chat token received:', {
    //         userId: data.userId,
    //         room: currentRoom.room_name
    //       });
    //     }
    //   } catch (error) {
    //     console.error('[Mobile] Error fetching chat token:', error);
    //   } finally {
    //     if (isMounted) {
    //       setLoading(false);
    //     }
    //   }
    // };

    // fetchChatToken();

    return () => {
      isMounted = false;
    };
  }, [currentRoom, accessToken, eventId]);

  const handleRoomSelect = (room: Room) => {
    // Check if this is a call room - if so, open the URL instead of switching rooms
    if (callRoomLinks[room.room_id]) {
      console.log('[Mobile] Call room selected, opening:', callRoomLinks[room.room_id]);
      window.open(callRoomLinks[room.room_id], '_blank', 'noopener,noreferrer');
      setShowMenu(false);
      return;
    }

    console.log('[Mobile] Switching to room:', room.room_name);
    setCurrentRoom(room);
    // setChatRoomArn(room.chat_room_arn);
    setShowMenu(false);
  };

  const handleToggleStageMute = () => {
    setIsStageMuted(!isStageMuted);
  };

  // Determine actual status based on current UTC time
  const getActualStatus = (item: AgendaItem): 'upcoming' | 'ongoing' | 'completed' => {
    const now = new Date();
    const itemStartTime = new Date(item.time_utc);
    const durationMs = (item.duration_minutes || 60) * 60 * 1000; // Default 60 minutes
    const itemEndTime = new Date(itemStartTime.getTime() + durationMs);

    if (now < itemStartTime) {
      return 'upcoming';
    } else if (now >= itemStartTime && now <= itemEndTime) {
      return 'ongoing';
    } else {
      return 'completed';
    }
  };

  const formatTime = (utcTimeString: string) => {
    const date = new Date(utcTimeString);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes} UTC`;
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'ongoing':
        return 'Live Now';
      case 'upcoming':
        return 'Upcoming';
      default:
        return status;
    }
  };

  const handleAgendaItemClick = (item: AgendaItem) => {
    // Only handle click if item has a room_id
    if (!item.room_id) return;
    
    // Get actual status based on UTC time
    const actualStatus = getActualStatus(item);
    
    // Only allow joining if item is ongoing
    if (actualStatus !== 'ongoing') return;
    
    // Don't navigate if already in this room
    if (item.room_id === currentRoom?.room_id) return;
    
    // Find the room from rooms list
    const room = rooms.find(r => r.room_id === item.room_id);
    if (!room) {
      console.warn('[Mobile Agenda] Room not found:', item.room_id);
      return;
    }
    
    // Switch to the room
    handleRoomSelect(room);
  };

  // Get current "happening now" agenda item (use actual status)
  const happeningNowItem = agendaItems
    .filter(item => {
      const actualStatus = getActualStatus(item);
      return actualStatus === 'ongoing';
    })
    .sort((a, b) => new Date(a.time_utc).getTime() - new Date(b.time_utc).getTime())[0];
  
  const upcomingItems = agendaItems
    .filter(item => {
      const actualStatus = getActualStatus(item);
      return actualStatus === 'upcoming';
    })
    .sort((a, b) => new Date(a.time_utc).getTime() - new Date(b.time_utc).getTime())
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col">
      {/* Header with Room Name and Menu Button */}
      <div className="bg-[#1a1a1a] border-b border-[#333] p-4 flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-lg font-bold text-white">Intersect Virtual Hub</h1>
          {currentRoom && (
            <p className="text-sm text-gray-400">{currentRoom.room_name}</p>
          )}
        </div>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="p-3 hover:bg-[#2a2a2a] rounded-lg transition-colors"
        >
          {showMenu ? <FaTimes size={24} /> : <FaBars size={24} />}
        </button>
      </div>

      {/* Hamburger Menu Overlay */}
      {showMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 overflow-y-auto">
          <div className="p-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Select Room</h2>
              <button
                onClick={() => setShowMenu(false)}
                className="p-3 hover:bg-[#2a2a2a] rounded-lg transition-colors"
              >
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="space-y-3">
              {rooms
                .filter((room) => {
                  const isCallRoom = !!callRoomLinks[room.room_id];
                  // Show call rooms always, but for regular rooms only show if they have a stage stream
                  return isCallRoom || room.stage_playback_url;
                })
                .map((room) => {
                const isCallRoom = !!callRoomLinks[room.room_id];
                return (
                  <button
                    key={room.room_id}
                    onClick={() => handleRoomSelect(room)}
                    className={`w-full text-left p-4 rounded-lg transition-colors ${
                      currentRoom?.room_id === room.room_id
                        ? 'bg-purple-600 text-white'
                        : 'bg-[#2a2a2a] text-white hover:bg-[#333]'
                    }`}
                  >
                    <div className="font-semibold">{room.room_name}</div>
                    <div className="text-sm text-gray-300 mt-1">{room.room_id}</div>
                    {isCallRoom && (
                      <div className="text-xs text-blue-400 mt-1">🔗 Opens Google Meet</div>
                    )}
                    {!isCallRoom && room.stage_playback_url && (
                      <div className="text-xs text-green-400 mt-1">● Live Stream Available</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {/* Stream Player */}
        {currentRoom?.stage_playback_url && (
          <div className="w-full aspect-video bg-black">
            <StagePlayer
              playbackUrl={currentRoom.stage_playback_url}
              isFullscreen={false}
              isMuted={isStageMuted}
              onToggleMute={handleToggleStageMute}
              onMinimize={() => {}}
            />
          </div>
        )}

        {!currentRoom?.stage_playback_url && (
          <div className="w-full aspect-video bg-[#1a1a1a] flex items-center justify-center">
            <div className="text-center p-8">
              <p className="text-gray-400 text-lg">No stream available for this room</p>
              <p className="text-gray-500 text-sm mt-2">Select a different room from the menu</p>
            </div>
          </div>
        )}

        {/* Happening Now Section with Expandable Upcoming */}
        {/* {happeningNowItem && (
          <div className="bg-[#1a1a1a] border-b border-[#333] p-4">
            <h3 className="text-sm font-semibold text-purple-400 mb-2">HAPPENING NOW</h3>
            <div 
              onClick={() => handleAgendaItemClick(happeningNowItem)}
              className={`bg-[#2a2a2a] rounded-lg p-4 ${
                happeningNowItem.room_id && happeningNowItem.room_id !== currentRoom?.room_id 
                  ? 'cursor-pointer hover:bg-[#333] border border-purple-600' 
                  : happeningNowItem.room_id === currentRoom?.room_id
                    ? 'border border-green-500'
                    : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <div className="text-xs text-gray-400">
                  {formatTime(happeningNowItem.time_utc)}
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-500 text-white animate-pulse">
                  Live Now
                </span>
                {happeningNowItem.room_id && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                    happeningNowItem.room_id === currentRoom?.room_id 
                      ? 'bg-green-500 text-white'
                      : 'bg-purple-600 text-white'
                  }`}>
                    <FaDoorOpen size={10} />
                    {happeningNowItem.room_id === currentRoom?.room_id ? 'Current Room' : 'Tap to Join'}
                  </span>
                )}
              </div>
              <h4 className="text-lg font-bold text-white mb-1">{happeningNowItem.title}</h4>
              <p className="text-sm text-gray-300">{happeningNowItem.subtitle}</p>
            </div> */}

            {/* Show More Button and Upcoming Items */}
            {/* {upcomingItems.length > 0 && (
              <div className="mt-3">
                <button
                  onClick={() => setShowUpcoming(!showUpcoming)}
                  className="w-full text-center py-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {showUpcoming ? '▼ Show Less' : `▶ Show ${upcomingItems.length} More`}
                </button>
                
                {showUpcoming && (
                  <div className="space-y-2 mt-2">
                    {upcomingItems.map((item) => {
                      const hasRoom = !!item.room_id;
                      const isCurrentRoom = item.room_id === currentRoom?.room_id;
                      
                      return (
                        <div 
                          key={item.id} 
                          className={`bg-[#2a2a2a] rounded-lg p-3 border-l-2 border-gray-600 ${
                            isCurrentRoom ? 'border-green-500' : ''
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <div className="text-xs text-gray-400">
                              {formatTime(item.time_utc)}
                            </div>
                            <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-blue-500 text-white">
                              Upcoming
                            </span>
                            {hasRoom && (
                              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold flex items-center gap-1 ${
                                isCurrentRoom 
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-600 text-gray-400'
                              }`}>
                                <FaDoorOpen size={10} />
                                {isCurrentRoom ? 'Current Room' : 'Not Available'}
                              </span>
                            )}
                          </div>
                          <h4 className="text-sm font-semibold text-white">{item.title}</h4>
                          {item.subtitle && (
                            <p className="text-xs text-gray-400 mt-1">{item.subtitle}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )} */}

        {/* Chat Section - Fixed for mobile */}
        {/* {currentRoom && !loading && chatRoomArn && (
          <div className="bg-[#0a0a0a] p-4">
            <h3 className="text-sm font-semibold text-gray-400 mb-3">CHAT</h3>
            <div className="relative h-[500px]" style={{ position: 'relative' }}>
              <div className="mobile-chat-container">
                <IVSRealtimeChat
                  key={`chat-${currentRoom.room_id}`}
                  roomArn={chatRoomArn}
                  messagingEndpoint="wss://edge.ivschat.us-west-2.amazonaws.com"
                  accessToken={accessToken}
                  roomName={currentRoom.room_name}
                  roomId={currentRoom.room_id}
                  eventId={eventId}
                  userId={userId}
                  podiumPosition={currentRoom.podium_position}
                />
              </div>
            </div>
          </div>
        )} */}

        {/* {loading && (
          <div className="bg-[#0a0a0a] p-8 text-center">
            <p className="text-gray-400">Loading chat...</p>
          </div>
        )} */}
      </div>

      {/* Footer */}
      <div className="bg-[#1a1a1a] border-t border-[#333] p-4 text-center">
        <p className="text-xs text-gray-500">
          To join the 3D virtual environment, please switch to desktop
        </p>
      </div>
    </div>
  );
};

export default IntersectEventMobile;

